import { useEffect, useState } from 'react';
import { Box, Typography, Button, Paper, IconButton, Dialog, DialogContent, TextField, Alert } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import FilterListIcon from '@mui/icons-material/FilterList';
import supabase from '../../supabaseClient';
import { formatCurrency } from '../../Components/Manager/Orders/currency';

const emptyForm = { name: '', nip: '', regon: '', contact_person: '', phone: '', email: '' };

function ManagerContractors() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [ordersByClient, setOrdersByClient] = useState({});

    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [form, setForm] = useState(emptyForm);
    const [saving, setSaving] = useState(false);
    const [errorMsg, setErrorMsg] = useState('');

    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const [deleteErrorMsg, setDeleteErrorMsg] = useState('');

    const [idConfiguration, setIdConfiguration] = useState(null);

    // Stany dla okna dialogowego filtrów
    const [filterOpen, setFilterOpen] = useState(false);
    const [searchName, setSearchName] = useState('');
    const [searchNip, setSearchNip] = useState('');
    const [searchEmail, setSearchEmail] = useState('');
    const [searchRegon, setSearchRegon] = useState('');
    const [searchPhone, setSearchPhone] = useState('');

    // Pobranie id_configuration zalogowanego użytkownika z jego profilu — zamiast wartości na sztywno
    useEffect(() => {
        const getInitialData = async () => {
            const { data: { session } } = await supabase.auth.getSession();
            if (session) {
                const { data: profileData } = await supabase
                    .from('profiles')
                    .select('id_configuration')
                    .eq('id', session.user.id)
                    .single();

                if (profileData) {
                    setIdConfiguration(profileData.id_configuration);
                }
            }
        };
        getInitialData();
    }, []);

    async function fetchContractors() {
        setLoading(true);
        const { data, error } = await supabase
            .from('kontrahenci')
            .select('*')
            .eq('id_configuration', idConfiguration)
            .order('name');

        if (error) {
            console.error('Błąd pobierania kontrahentów:', error);
            setLoading(false);
            return;
        }

        setRows(data);
        setLoading(false);
    }

    // Sumowanie wartości zamówień per kontrahent (w podziale na walutę), do statystyk rentowności klientów
    async function fetchOrderTotals() {
        const { data, error } = await supabase
            .from('orders')
            .select('client_id, total_price, currency')
            .eq('id_configuration', idConfiguration);

        if (error) {
            console.error('Błąd pobierania wartości zamówień:', error);
            return;
        }

        const totals = {};
        (data ?? []).forEach((order) => {
            if (!order.client_id) return;
            const currency = order.currency || 'PLN';
            if (!totals[order.client_id]) totals[order.client_id] = {};
            totals[order.client_id][currency] = (totals[order.client_id][currency] || 0) + (order.total_price || 0);
        });

        setOrdersByClient(totals);
    }

    // Dane pobieramy dopiero, gdy znamy id_configuration użytkownika
    useEffect(() => {
        if (idConfiguration) {
            fetchContractors();
            fetchOrderTotals();
        }
    }, [idConfiguration]);

    const openAddDialog = () => {
        setEditingId(null);
        setForm(emptyForm);
        setErrorMsg('');
        setDialogOpen(true);
    };

    const openEditDialog = (row) => {
        setEditingId(row.id);
        setForm({
            name: row.name ?? '',
            nip: row.nip ?? '',
            regon: row.regon ?? '',
            contact_person: row.contact_person ?? '',
            phone: row.phone ?? '',
            email: row.email ?? '',
        });
        setErrorMsg('');
        setDialogOpen(true);
    };

    const handleFieldChange = (field) => (e) => {
        setForm((prev) => ({ ...prev, [field]: e.target.value }));
    };

    const handleSave = async () => {
        if (!form.name.trim()) {
            setErrorMsg('Nazwa kontrahenta jest wymagana.');
            return;
        }

        if (!idConfiguration) {
            setErrorMsg('Nie udało się ustalić konfiguracji użytkownika — odśwież stronę i spróbuj ponownie.');
            return;
        }

        setSaving(true);
        setErrorMsg('');

        const payload = {
            name: form.name.trim(),
            nip: form.nip.trim() || null,
            regon: form.regon.trim() || null,
            contact_person: form.contact_person.trim() || null,
            phone: form.phone.trim() || null,
            email: form.email.trim() || null,
        };

        const { error } = editingId
            ? await supabase.from('kontrahenci').update(payload).eq('id', editingId)
            : await supabase.from('kontrahenci').insert({ ...payload, id_configuration: idConfiguration });

        setSaving(false);

        if (error) {
            console.error('Błąd zapisu kontrahenta:', error);
            setErrorMsg('Nie udało się zapisać kontrahenta.');
            return;
        }

        setDialogOpen(false);
        fetchContractors();
    };

    const handleDelete = async () => {
        if (!deleteTarget) return;

        setDeleting(true);
        setDeleteErrorMsg('');

        const { error } = await supabase.from('kontrahenci').delete().eq('id', deleteTarget.id);
        
        setDeleting(false);

        if (error) {
            console.error('Błąd usuwania kontrahenta:', error);
            setDeleteErrorMsg('Nie można usunąć kontrahenta, ponieważ jest on powiązany z innymi danymi w systemie.');
            return;
        }

        setDeleteTarget(null);
        setDeleteErrorMsg('');
        fetchContractors();
    };

    const filteredRows = rows.filter((row) => {
        const matchesName = searchName === '' || (row.name && row.name.toLowerCase().includes(searchName.toLowerCase()));
        const matchesNip = searchNip === '' || (row.nip && row.nip.toLowerCase().includes(searchNip.toLowerCase()));
        const matchesEmail = searchEmail === '' || (row.email && row.email.toLowerCase().includes(searchEmail.toLowerCase()));
        const matchesRegon = searchRegon === '' || (row.regon && row.regon.toLowerCase().includes(searchRegon.toLowerCase()));
        const matchesPhone = searchPhone === '' || (row.phone && row.phone.toLowerCase().includes(searchPhone.toLowerCase()));

        return matchesName && matchesNip && matchesEmail && matchesRegon && matchesPhone;
    });

    const columns = [
        { field: 'name', headerName: 'NAZWA', flex: 1.4, minWidth: 180, renderCell: (params) => <strong>{params.value}</strong> },
        { field: 'nip', headerName: 'NIP', flex: 0.9, minWidth: 110 },
        { field: 'regon', headerName: 'REGON', flex: 0.9, minWidth: 110 },
        { field: 'contact_person', headerName: 'OSOBA KONTAKTOWA', flex: 1.2, minWidth: 150 },
        { field: 'phone', headerName: 'TELEFON', flex: 1, minWidth: 130 },
        { field: 'email', headerName: 'E-MAIL', flex: 1.3, minWidth: 170 },
        {
            field: 'orders_total',
            headerName: 'WARTOŚĆ ZAMÓWIEŃ',
            flex: 1.2,
            minWidth: 170,
            valueGetter: (value, row) => {
                const totals = ordersByClient[row.id] || {};
                return Object.values(totals).reduce((sum, v) => sum + v, 0);
            },
            renderCell: (params) => {
                const totals = ordersByClient[params.row.id];
                if (!totals || Object.keys(totals).length === 0) {
                    return <span style={{ color: '#B3ABA0' }}>—</span>;
                }
                return (
                    <strong>
                        {Object.entries(totals).map(([currency, value]) => formatCurrency(value, currency)).join(' + ')}
                    </strong>
                );
            },
        },
        {
            field: 'actions',
            headerName: '',
            flex: 0.6,
            minWidth: 90,
            sortable: false,
            renderCell: (params) => (
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); openEditDialog(params.row); }}>
                        <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); setDeleteTarget(params.row); setDeleteErrorMsg(''); }}>
                        <DeleteIcon fontSize="small" />
                    </IconButton>
                </Box>
            ),
        },
    ];

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography sx={{ fontSize: '24px', fontWeight: 700, color: 'primary.main' }}>Kontrahenci</Typography>
                <Box sx={{ display: 'flex', gap: 1.5 }}>
                    <Button
                        variant="outlined"
                        onClick={() => setFilterOpen(true)}
                        startIcon={<FilterListIcon />}
                        sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '10px', px: 2, py: 1 }}
                    >
                        Filtry
                    </Button>
                    <Button
                        variant="contained"
                        onClick={openAddDialog}
                        sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '10px', px: 2.5, py: 1 }}
                    >
                        + Nowy kontrahent
                    </Button>
                </Box>
            </Box>

            <Paper elevation={0} sx={{ borderRadius: 3, overflow: 'hidden', border: '1px solid #ECE7DE' }}>
                <DataGrid
                    rows={filteredRows}
                    columns={columns}
                    loading={loading}
                    onRowClick={(params) => openEditDialog(params.row)}
                    disableRowSelectionOnClick
                    disableColumnMenu
                    initialState={{ sorting: { sortModel: [{ field: 'orders_total', sort: 'desc' }] } }}
                    hideFooter={filteredRows.length <= 100}
                    columnHeaderHeight={44}
                    sx={{
                        border: 'none',
                        '& .MuiDataGrid-row': { cursor: 'pointer' },
                        '& .MuiDataGrid-columnHeaders': { backgroundColor: '#FAF7F2' },
                        '& .MuiDataGrid-columnHeaderTitle': { color: 'text.secondary', fontWeight: 700, fontSize: '12px', letterSpacing: '0.03em' },
                        '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': { outline: 'none' },
                        '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within': { outline: 'none' },
                        '& .MuiDataGrid-row:hover': { backgroundColor: '#FEFCF9' },
                    }}
                />
            </Paper>

            {/* Dodawanie / edycja */}
            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
                <DialogContent sx={{ p: 4 }}>
                    <Typography sx={{ fontSize: '20px', fontWeight: 700, color: 'primary.main', mb: 3 }}>
                        {editingId ? 'Edytuj kontrahenta' : 'Nowy kontrahent'}
                    </Typography>

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
                        <TextField label="Nazwa" value={form.name} onChange={handleFieldChange('name')} size="small" fullWidth />
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <TextField label="NIP" value={form.nip} onChange={handleFieldChange('nip')} size="small" fullWidth />
                            <TextField label="REGON" value={form.regon} onChange={handleFieldChange('regon')} size="small" fullWidth />
                        </Box>
                        <TextField label="Osoba kontaktowa" value={form.contact_person} onChange={handleFieldChange('contact_person')} size="small" fullWidth />
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <TextField label="Telefon" value={form.phone} onChange={handleFieldChange('phone')} size="small" fullWidth />
                            <TextField label="E-mail" value={form.email} onChange={handleFieldChange('email')} size="small" fullWidth />
                        </Box>
                    </Box>

                    {errorMsg && <Typography sx={{ color: '#C7433F', fontSize: '13px', mb: 2 }}>{errorMsg}</Typography>}

                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5 }}>
                        <Button onClick={() => setDialogOpen(false)} disabled={saving} sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '10px', px: 2.5 }}>
                            Anuluj
                        </Button>
                        <Button variant="contained" onClick={handleSave} disabled={saving} sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '10px', px: 2.5 }}>
                            {saving ? 'Zapisywanie...' : 'Zapisz'}
                        </Button>
                    </Box>
                </DialogContent>
            </Dialog>

            {/* Okno dialogowe filtrów */}
            <Dialog open={filterOpen} onClose={() => setFilterOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
                <DialogContent sx={{ p: 4 }}>
                    <Typography sx={{ fontSize: '20px', fontWeight: 700, color: 'primary.main', mb: 3 }}>
                        Filtry
                    </Typography>

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
                        <TextField
                            label="Nazwa"
                            variant="outlined"
                            size="small"
                            fullWidth
                            value={searchName}
                            onChange={(e) => setSearchName(e.target.value)}
                        />
                        <TextField
                            label="NIP"
                            variant="outlined"
                            size="small"
                            fullWidth
                            value={searchNip}
                            onChange={(e) => setSearchNip(e.target.value)}
                        />
                        <TextField
                            label="REGON"
                            variant="outlined"
                            size="small"
                            fullWidth
                            value={searchRegon}
                            onChange={(e) => setSearchRegon(e.target.value)}
                        />
                        <TextField
                            label="Telefon"
                            variant="outlined"
                            size="small"
                            fullWidth
                            value={searchPhone}
                            onChange={(e) => setSearchPhone(e.target.value)}
                        />
                        <TextField
                            label="E-mail"
                            variant="outlined"
                            size="small"
                            fullWidth
                            value={searchEmail}
                            onChange={(e) => setSearchEmail(e.target.value)}
                        />
                    </Box>

                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5 }}>
                        <Button onClick={() => setFilterOpen(false)} sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '10px', px: 2.5 }}>
                            Zamknij
                        </Button>
                        <Button variant="contained" onClick={() => setFilterOpen(false)} sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '10px', px: 2.5 }}>
                            Zastosuj
                        </Button>
                    </Box>
                </DialogContent>
            </Dialog>

            {/* Potwierdzenie usunięcia */}
            <Dialog open={Boolean(deleteTarget)} onClose={() => { setDeleteTarget(null); setDeleteErrorMsg(''); }} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
                <DialogContent sx={{ p: 4 }}>
                    <Typography sx={{ fontSize: '18px', fontWeight: 700, mb: 1 }}>Usunąć kontrahenta?</Typography>
                    <Typography sx={{ color: 'text.secondary', mb: 3 }}>
                        {deleteTarget?.name} — tej operacji nie można cofnąć.
                    </Typography>

                    {deleteErrorMsg && (
                        <Alert severity="error" sx={{ mb: 3 }}>
                            {deleteErrorMsg}
                        </Alert>
                    )}

                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5 }}>
                        <Button onClick={() => { setDeleteTarget(null); setDeleteErrorMsg(''); }} disabled={deleting} sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '10px', px: 2.5 }}>
                            Anuluj
                        </Button>
                        <Button
                            variant="contained"
                            color="error"
                            onClick={handleDelete}
                            disabled={deleting}
                            sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '10px', px: 2.5 }}
                        >
                            {deleting ? 'Usuwanie...' : 'Usuń'}
                        </Button>
                    </Box>
                </DialogContent>
            </Dialog>
        </Box>
    );
}

export default ManagerContractors;